import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import axiosAPI from "../axiosAPI";

function buildPaymentsQuery(filter = {}) {
  const params = new URLSearchParams();
  if (filter.start_date) params.set("start_date", filter.start_date);
  if (filter.end_date) params.set("end_date", filter.end_date);
  if (filter.category) params.set("category", filter.category);
  if (filter.lawsuit) params.set("lawsuit_id", filter.lawsuit);
  if (filter.clientId) params.set("client_id", filter.clientId);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function normalizePayments(data) {
  if (Array.isArray(data?.payments)) return data.payments;
  if (Array.isArray(data?.payment)) return data.payment;
  return [];
}

export const useSendPaymentReminder = () => {
  const mutation = useMutation({
    mutationFn: async (paymentId) => {
      const { data } = await axiosAPI.post(
        `/payments/${paymentId}/send-reminder`
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || "E-mail de cobrança enviado.", {
        position: "bottom-right",
      });
    },
    onError: (error) => {
      // axiosAPI rejects with the Axios response object (not Error).
      const message =
        error?.data?.message ||
        error?.response?.data?.message ||
        "Ops, ocorreu um erro ao enviar o e-mail de cobrança.";
      toast.error(message, { position: "bottom-right" });
    },
  });

  return {
    sendReminder: mutation.mutateAsync,
    isSendingReminder: mutation.isLoading,
  };
};

export const usePayment = (filter = {}) => {
  const queryClient = useQueryClient();
  const queryString = buildPaymentsQuery(filter);

  const query = useQuery({
    queryKey: ["payments", "list", queryString],
    queryFn: async () => {
      const { data } = await axiosAPI.get(`/payments${queryString}`);
      return data;
    },
  });

  const invalidatePayments = () => {
    queryClient.invalidateQueries({ queryKey: ["payments"] });
    queryClient.invalidateQueries({ queryKey: ["financial"] });
    queryClient.invalidateQueries({ queryKey: ["lawsuit-financial-summary"] });
  };

  const createMutation = useMutation({
    mutationFn: async (payload) => {
      const { data } = await axiosAPI.post("/payments", payload);
      return data;
    },
    onSuccess: () => {
      invalidatePayments();
      toast.success("Cobrança cadastrada.", { position: "bottom-right" });
    },
    onError: () => {
      toast.error("Ops, ocorreu um erro ao cadastrar a cobrança.");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const { data } = await axiosAPI.put(`/payments/${id}`, payload);
      return data;
    },
    onSuccess: () => {
      invalidatePayments();
      toast.success("Cobrança atualizada.", { position: "bottom-right" });
    },
    onError: () => {
      toast.error("Ops, ocorreu um erro ao atualizar a cobrança.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (paymentId) => {
      const { data } = await axiosAPI.delete(`/payments/${paymentId}`);
      return data;
    },
    onSuccess: () => {
      invalidatePayments();
      toast.success("Cobrança excluída.", { position: "bottom-right" });
    },
    onError: () => {
      toast.error("Ops, ocorreu um erro ao excluir a cobrança.");
    },
  });

  const { sendReminder, isSendingReminder } = useSendPaymentReminder();

  return {
    payments: normalizePayments(query.data),
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.error,
    refetch: query.refetch,
    createPayment: createMutation.mutateAsync,
    updatePayment: updateMutation.mutateAsync,
    deletePayment: deleteMutation.mutateAsync,
    sendReminder,
    isCreating: createMutation.isLoading,
    isUpdating: updateMutation.isLoading,
    isDeleting: deleteMutation.isLoading,
    isSendingReminder,
    isMutating:
      createMutation.isLoading ||
      updateMutation.isLoading ||
      deleteMutation.isLoading ||
      isSendingReminder,
  };
};
